import { useEffect, useRef, useCallback } from 'react';
import { DashboardTab } from '@/types/dashboard';
import { logger } from '@/utils/logger';

interface NavigationHistoryState {
  _learnbahn: true;
  tab: string;
  subView?: {
    selectedUserId?: string | null;
  };
  index: number;
}

interface SubViewPatch {
  selectedUserId?: string | null;
}

interface UseNavigationHistoryOptions {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  peopleSelectedUserId: string | null;
  setPeopleSelectedUserId: (id: string | null) => void;
}

function isLearnbahnState(state: unknown): state is NavigationHistoryState {
  return (
    typeof state === 'object' &&
    state !== null &&
    (state as NavigationHistoryState)._learnbahn === true
  );
}

export function useNavigationHistory({
  activeTab,
  setActiveTab,
  peopleSelectedUserId,
  setPeopleSelectedUserId,
}: UseNavigationHistoryOptions) {
  const indexRef = useRef(1);
  const isPoppingRef = useRef(false);

  // Initialize history on mount: guard entry (index 0) + initial position (index 1)
  useEffect(() => {
    const guardState: NavigationHistoryState = {
      _learnbahn: true,
      tab: activeTab,
      subView: { selectedUserId: peopleSelectedUserId },
      index: 0,
    };
    window.history.replaceState(guardState, '');

    const initialState: NavigationHistoryState = {
      _learnbahn: true,
      tab: activeTab,
      subView: { selectedUserId: peopleSelectedUserId },
      index: 1,
    };
    window.history.pushState(initialState, '');
    indexRef.current = 1;

    logger.debug('[NavigationHistory] Initialized guard + initial state', {
      tab: activeTab,
    });
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for popstate (browser back/forward)
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      const state = event.state;

      if (!isLearnbahnState(state)) {
        // User hit the guard entry (or went beyond) — re-push guard to prevent leaving the app
        logger.debug('[NavigationHistory] Guard hit, re-pushing');
        const guardState: NavigationHistoryState = {
          _learnbahn: true,
          tab: activeTab,
          subView: { selectedUserId: peopleSelectedUserId },
          index: 0,
        };
        window.history.pushState(guardState, '');
        return;
      }

      logger.debug('[NavigationHistory] Popstate restoring', {
        tab: state.tab,
        subView: state.subView,
        index: state.index,
      });

      isPoppingRef.current = true;
      indexRef.current = state.index;

      // Restore state using raw setters (no history push)
      setActiveTab(state.tab);
      setPeopleSelectedUserId(state.subView?.selectedUserId ?? null);

      // Unlock after the current event cycle completes
      requestAnimationFrame(() => {
        isPoppingRef.current = false;
      });
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [activeTab, peopleSelectedUserId, setActiveTab, setPeopleSelectedUserId]);

  const navigateToTab = useCallback(
    (tab: string) => {
      if (isPoppingRef.current) return;
      if (tab === activeTab) return;

      const nextIndex = indexRef.current + 1;
      const state: NavigationHistoryState = {
        _learnbahn: true,
        tab,
        // Preserve peopleSelectedUserId when navigating to the People tab so
        // that popstate can restore the profile view (e.g. browser back from MY_BOARD)
        subView: {
          selectedUserId:
            tab === DashboardTab.PEOPLE ? peopleSelectedUserId : null,
        },
        index: nextIndex,
      };

      window.history.pushState(state, '');
      indexRef.current = nextIndex;

      logger.debug('[NavigationHistory] Pushed tab', { tab, index: nextIndex });

      setActiveTab(tab);
    },
    [activeTab, peopleSelectedUserId, setActiveTab]
  );

  const navigateToSubView = useCallback(
    (patch: SubViewPatch) => {
      if (isPoppingRef.current) return;

      const nextIndex = indexRef.current + 1;
      const state: NavigationHistoryState = {
        _learnbahn: true,
        tab: activeTab,
        subView: {
          selectedUserId:
            patch.selectedUserId !== undefined
              ? patch.selectedUserId
              : peopleSelectedUserId,
        },
        index: nextIndex,
      };

      window.history.pushState(state, '');
      indexRef.current = nextIndex;

      logger.debug('[NavigationHistory] Pushed subView', {
        patch,
        index: nextIndex,
      });

      if (patch.selectedUserId !== undefined) {
        setPeopleSelectedUserId(patch.selectedUserId);
      }
    },
    [activeTab, peopleSelectedUserId, setPeopleSelectedUserId]
  );

  return { navigateToTab, navigateToSubView };
}
